import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import {
  AlertCircle,
  Camera,
  Check,
  Loader2,
  Receipt as ReceiptIcon,
  RotateCcw,
  ArrowLeft,
  ArrowDownToLine,
  ImageOff,
  ImagePlus,
  Split,
  Plus,
  Trash2,
} from 'lucide-react';
import {
  STORES,
  STORE_REBATES,
  receiptRebate,
  splitReceiptByJob,
  fileToDataUrl,
  scanReceipt,
  formatUsd,
  logReceipt,
} from '@/lib/receiptsData';
import { capturePriceBookFromScan } from '@/lib/priceBookData';
import {
  JOB_BUCKETS,
  resolveBucketJob,
  isBucketOption,
  bucketOptionName,
} from '@/lib/jobsData';
import { Sparkles } from 'lucide-react';
import ReceiptCameraView from '@/components/receipts/ReceiptCameraView';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

// Visible build tag — lets us confirm which bundle a device is actually
// running (bump alongside the service-worker VERSION on each deploy).
const APP_BUILD = 'v88';

let _itemSeq = 0;
const itemId = () => {
  _itemSeq += 1;
  return `it-${Date.now().toString(36)}-${_itemSeq}`;
};

/**
 * Capture-receipt dialog — two-step flow:
 *   Step 1 ("photo"): take a photo → see preview → "Use Photo" advances.
 *                     Optional "Skip photo" advances without an image.
 *   Step 2 ("form"):  fill receipt details and Save. Photo thumbnail is shown
 *                     at the top with a "Change photo" link to step back.
 */
export const LogReceiptDialog = ({
  open,
  onOpenChange,
  jobs = [],
  defaultJobId = '',
  onLogged,
}) => {
  const { t } = useTranslation();
  const [step, setStep] = useState('photo'); // 'photo' | 'form' | 'split'
  const [jobId, setJobId] = useState(defaultJobId);
  // Split mode — each line item carries the job it should be charged to.
  const [lineItems, setLineItems] = useState([]); // [{ id, desc, amount, jobId }]
  const [store, setStore] = useState('Menards');
  const [subtotal, setSubtotal] = useState('');
  const [tax, setTax] = useState('');
  const [rebateAmount, setRebateAmount] = useState(null); // printed rebate $ (Menards)
  const [rebateNumber, setRebateNumber] = useState('');
  const [desc, setDesc] = useState('');
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [photo, setPhoto] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [readingPhoto, setReadingPhoto] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [scanInfo, setScanInfo] = useState(null); // { confidence } after a scan
  const [scanFailed, setScanFailed] = useState(false); // auto-read didn't work
  // True from the moment the camera is launched until we're safely back —
  // used to swallow the spurious close the phone camera fires on return.
  const [cameraPending, setCameraPending] = useState(false);
  // In-app live viewfinder (getUserMedia) — everything stays inside the app,
  // so Android can't evict/reload the page mid-capture.
  const [cameraOpen, setCameraOpen] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (open) {
      setStep('photo');
      setJobId(defaultJobId || '');
      setLineItems([]);
      setStore('Menards');
      setSubtotal('');
      setTax('');
      setRebateAmount(null);
      setRebateNumber('');
      setDesc('');
      setDate(new Date().toISOString().slice(0, 10));
      setPhoto(null);
      setError('');
      setReadingPhoto(false);
      setScanning(false);
      setScanInfo(null);
      setScanFailed(false);
      setCameraPending(false);
      setCameraOpen(false);
    }
  }, [open, defaultJobId]);

  const subNum = Number(subtotal) || 0;
  const taxNum = Number(tax) || 0;
  const totalNum = Math.round((subNum + taxNum) * 100) / 100;
  const rebate = receiptRebate({ store, subtotal: subNum, total: totalNum, tax: taxNum, rebateAmount });
  const ratePct = Math.round((STORE_REBATES[store] || 0) * 100);
  const rebateFromReceipt = Number(rebateAmount) > 0;

  // Split-mode computed values -------------------------------------------
  const jobLabelById = (id) => {
    if (isBucketOption(id)) return bucketOptionName(id);
    return jobs.find((j) => j.id === id)?.name || 'Unassigned';
  };
  const itemsTotal =
    Math.round(
      lineItems.reduce((s, it) => s + (Number(it.amount) || 0), 0) * 100
    ) / 100;
  const allItemsAssigned =
    lineItems.length > 0 &&
    lineItems.every((it) => it.jobId && Number(it.amount) > 0);
  const splitGroups = splitReceiptByJob(
    lineItems.map((it) => ({
      desc: it.desc,
      amount: Number(it.amount) || 0,
      jobId: it.jobId,
    })),
    { tax: taxNum, rebateAmount }
  );

  const triggerCamera = () => {
    if (!fileRef.current) return;
    // Arm the guard: while the native camera is open the dialog must not close.
    // When we return (window regains focus), keep the guard up briefly so the
    // camera's spurious close event is swallowed, then release it — this also
    // covers the case where the user cancels the camera (no file selected).
    setCameraPending(true);
    const release = () => {
      window.removeEventListener('focus', release);
      setTimeout(() => setCameraPending(false), 1200);
    };
    window.addEventListener('focus', release);
    fileRef.current.value = '';
    fileRef.current.click();
  };

  // Shared tail of the capture flow: takes a photo data URL (from the in-app
  // camera or the OS picker), stores it, scans it, and advances to the form.
  const processPhotoDataUrl = async (dataUrl) => {
    if (!dataUrl) return;
    setPhoto(dataUrl);

    // AI scan → pre-fill the form as a proposal the contractor confirms.
    // Non-blocking: any miss just leaves the fields for manual entry.
    setScanning(true);
    try {
      const scan = await scanReceipt(dataUrl);
      if (scan.ok) {
        if (scan.store) setStore(scan.store);
        if (scan.subtotal) setSubtotal(scan.subtotal);
        if (scan.tax) setTax(scan.tax);
        if (scan.rebateAmount != null) setRebateAmount(scan.rebateAmount);
        if (scan.rebateNumber) setRebateNumber(scan.rebateNumber);
        if (scan.date) setDate(scan.date);
        if (scan.desc) setDesc(scan.desc);
        if (Array.isArray(scan.lineItems) && scan.lineItems.length) {
          setLineItems(
            scan.lineItems.map((li) => ({
              id: itemId(),
              desc: li.desc,
              amount: String(li.amount),
              jobId: defaultJobId || '',
            }))
          );
        }
        setScanInfo({ confidence: scan.confidence });
        setStep('form'); // jump straight to the pre-filled form — the magic
      } else {
        // Couldn't auto-read — don't strand the user on the photo screen. Move
        // to the form (photo is kept) with a clear note to enter it by hand.
        setScanFailed(true);
        setStep('form');
      }
    } catch {
      setScanFailed(true);
      setStep('form');
    } finally {
      setScanning(false);
    }
  };

  // OS photo picker path: file → data URL → shared flow.
  const handlePhoto = async (file) => {
    setCameraPending(false);
    if (!file) return;
    setReadingPhoto(true);
    setError('');
    setScanInfo(null);
    setScanFailed(false);
    let dataUrl;
    try {
      dataUrl = await fileToDataUrl(file);
    } catch (e) {
      setError(e?.message || t('receiptScanner.dialog.couldNotRead'));
      setReadingPhoto(false);
      return;
    }
    setReadingPhoto(false);
    await processPhotoDataUrl(dataUrl);
  };

  // In-app camera path: the viewfinder hands us a ready data URL directly.
  const handleCameraCapture = async (dataUrl) => {
    setCameraOpen(false);
    setError('');
    setScanInfo(null);
    setScanFailed(false);
    await processPhotoDataUrl(dataUrl);
  };

  // Camera couldn't start (permission denied / no camera) — fall back to the
  // OS photo picker so the flow never dead-ends.
  const handleCameraUnavailable = () => {
    setCameraOpen(false);
    setError(t('receiptScanner.dialog.cameraFallback'));
    triggerCamera();
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    setError('');
    if (!isBucketOption(jobId) && !jobs.find((j) => j.id === jobId)) {
      setError(t('receiptScanner.dialog.pickJob'));
      return;
    }
    if (subNum <= 0) {
      setError(t('receiptScanner.dialog.positiveAmount'));
      return;
    }
    setSubmitting(true);
    try {
      // A bucket (Miscellaneous / Gas) is resolved (find-or-create) at save time
      // so we don't spin up a catch-all job until a receipt actually lands in it.
      const job = isBucketOption(jobId)
        ? await resolveBucketJob(jobId)
        : jobs.find((j) => j.id === jobId);
      const updated = await logReceipt(job, {
        store,
        amount: totalNum,
        subtotal: subNum,
        tax: taxNum,
        rebateAmount,
        rebateNumber,
        desc,
        date,
        photo,
      });
      // Price Book Phase 1: file the scanned line items per store — the
      // itemized gold used to be discarded here. Fire-and-forget: the book
      // must never block or fail the receipt save.
      void capturePriceBookFromScan({
        store,
        date,
        items: lineItems,
        sourceRef: job?.name || null,
      });
      toast.success(t('receiptScanner.dialog.loggedToast', { amount: formatUsd(rebate) }));
      onLogged?.(updated);
      onOpenChange?.(false);
    } catch (err) {
      setError(err?.message || t('receiptScanner.dialog.logFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  /* --------------------------------------------------------------------- */
  /* Split-across-jobs handlers                                            */
  /* --------------------------------------------------------------------- */
  const enterSplit = () => {
    setError('');
    // Seed the list if the scan found no items — start from the whole subtotal
    // as one line so the contractor can break it apart manually.
    if (lineItems.length === 0) {
      setLineItems([
        {
          id: itemId(),
          desc: desc || '',
          amount: subtotal || '',
          jobId: jobId || '',
        },
      ]);
    } else if (jobId) {
      // Default any still-unassigned items to the job chosen on the form.
      setLineItems((cur) =>
        cur.map((it) => (it.jobId ? it : { ...it, jobId }))
      );
    }
    setStep('split');
  };

  const updateItem = (id, patch) =>
    setLineItems((cur) => cur.map((it) => (it.id === id ? { ...it, ...patch } : it)));
  const removeItem = (id) =>
    setLineItems((cur) => cur.filter((it) => it.id !== id));
  const addItem = () =>
    setLineItems((cur) => [
      ...cur,
      { id: itemId(), desc: '', amount: '', jobId: jobId || '' },
    ]);

  const handleSaveSplit = async (e) => {
    e?.preventDefault?.();
    setError('');
    const priced = lineItems.filter((it) => Number(it.amount) > 0);
    if (priced.length === 0) {
      setError('Add at least one item with an amount.');
      return;
    }
    if (!priced.every((it) => it.jobId)) {
      setError('Assign every item to a job before saving.');
      return;
    }
    setSubmitting(true);
    try {
      const groups = splitReceiptByJob(
        priced.map((it) => ({
          desc: it.desc,
          amount: Number(it.amount) || 0,
          jobId: it.jobId,
        })),
        { tax: taxNum, rebateAmount }
      );
      let totalRebate = 0;
      // Resolve each bucket sentinel to its real job once, on demand.
      const bucketCache = new Map();
      for (const g of groups) {
        let job;
        if (isBucketOption(g.jobId)) {
          if (!bucketCache.has(g.jobId)) {
            bucketCache.set(g.jobId, await resolveBucketJob(g.jobId));
          }
          job = bucketCache.get(g.jobId);
        } else {
          job = jobs.find((j) => j.id === g.jobId);
        }
        if (!job) continue;
        const groupDesc = g.items
          .map((i) => i.desc)
          .filter(Boolean)
          .join(', ')
          .slice(0, 200);
        const updated = await logReceipt(job, {
          store,
          amount: Math.round((g.subtotal + g.tax) * 100) / 100,
          subtotal: g.subtotal,
          tax: g.tax,
          rebateAmount: g.rebateAmount,
          rebateNumber,
          desc: groupDesc,
          date,
          photo,
        });
        totalRebate += receiptRebate({
          store,
          subtotal: g.subtotal,
          tax: g.tax,
          rebateAmount: g.rebateAmount,
        });
        onLogged?.(updated);
      }
      void capturePriceBookFromScan({
        store,
        date,
        items: priced,
        sourceRef: 'split receipt',
      });
      toast.success(
        `Split across ${groups.length} job${groups.length > 1 ? 's' : ''} — ${formatUsd(
          totalRebate
        )} rebate`
      );
      onOpenChange?.(false);
    } catch (err) {
      setError(err?.message || t('receiptScanner.dialog.logFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  /* --------------------------------------------------------------------- */
  /* Step 1 — Photo capture                                                */
  /* --------------------------------------------------------------------- */
  const renderPhotoStep = () => (
    <div className="space-y-4 mt-1" data-testid="receipt-step-photo">
      {/* No `capture` attribute on purpose: forcing the dedicated full-screen
          camera makes Android evict/reload the whole web app while the camera
          is open (losing the dialog + the photo). Letting the OS photo picker
          handle it — snap a new one or pick an existing shot — runs in a
          separate process and keeps our app alive. */}
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => handlePhoto(e.target.files?.[0])}
        data-testid="receipt-photo-input"
      />

      {cameraOpen ? (
        // Live in-app viewfinder — capture happens without leaving the page.
        <ReceiptCameraView
          onCapture={handleCameraCapture}
          onCancel={() => setCameraOpen(false)}
          onUnavailable={handleCameraUnavailable}
        />
      ) : photo ? (
        // Captured — show preview + advance/retake actions.
        <>
          <div
            className="rounded-md border border-border bg-[hsl(var(--muted))]/40 p-2"
            data-testid="receipt-photo-preview-wrap"
          >
            <img
              src={photo}
              alt="Receipt preview"
              className="block w-full max-h-[60vh] object-contain rounded"
              data-testid="receipt-photo-preview"
            />
          </div>
          {scanning ? (
            <div
              className="flex items-center justify-center gap-2 text-[12px] text-[hsl(var(--primary))]"
              data-testid="receipt-scanning"
            >
              <Sparkles size={13} className="animate-pulse" />
              Reading receipt…
            </div>
          ) : (
            <div className="text-[11px] text-center text-muted-foreground">
              {t('receiptScanner.dialog.useHintPrefix')}
              <span className="text-foreground font-semibold">{t('receiptScanner.dialog.useHintAction')}</span>
              {t('receiptScanner.dialog.useHintSuffix')}
            </div>
          )}
          <div className="grid grid-cols-2 gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setCameraOpen(true)}
              disabled={readingPhoto || scanning}
              className="h-11 border-border text-foreground hover:bg-[hsl(var(--muted))] uppercase tracking-[0.16em] text-xs"
              data-testid="receipt-photo-retake"
            >
              <RotateCcw size={14} className="mr-2" />
              {t('receiptScanner.dialog.retake')}
            </Button>
            <Button
              type="button"
              onClick={() => setStep('form')}
              disabled={readingPhoto || scanning}
              className="h-11 bg-primary text-primary-foreground hover:bg-primary/90 uppercase tracking-[0.16em] text-xs"
              data-testid="receipt-photo-use"
            >
              <Check size={14} className="mr-2" />
              {t('receiptScanner.dialog.usePhoto')}
            </Button>
          </div>
        </>
      ) : (
        // Empty — prompt to take a photo. Skip option available.
        <>
          <button
            type="button"
            onClick={() => setCameraOpen(true)}
            disabled={readingPhoto}
            className="w-full rounded-md border-2 border-dashed border-[hsl(var(--primary))]/30 bg-[hsl(var(--primary))]/5 hover:bg-[hsl(var(--primary))]/10 hover:border-[hsl(var(--primary))]/50 transition-colors py-12 px-4 flex flex-col items-center gap-3 text-[hsl(var(--primary))]"
            data-testid="receipt-photo-take"
          >
            {readingPhoto ? (
              <Loader2 size={32} className="animate-spin" />
            ) : (
              <Camera size={32} />
            )}
            <div className="text-sm font-semibold uppercase tracking-[0.18em]">
              {readingPhoto ? t('receiptScanner.dialog.readingPhoto') : t('receiptScanner.dialog.takePhoto')}
            </div>
            <div className="text-[11px] text-muted-foreground normal-case tracking-normal">
              {t('receiptScanner.dialog.cameraHint')}
            </div>
          </button>
          <div className="flex items-center justify-center gap-5">
            <button
              type="button"
              onClick={triggerCamera}
              disabled={readingPhoto}
              className="inline-flex items-center gap-1.5 text-xs text-[hsl(var(--primary))] underline-offset-4 hover:underline"
              data-testid="receipt-photo-pick"
            >
              <ImagePlus size={12} />
              {t('receiptScanner.dialog.pickFromPhotos')}
            </button>
            <button
              type="button"
              onClick={() => {
                setPhoto(null);
                setStep('form');
              }}
              disabled={readingPhoto}
              className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground underline-offset-4 hover:underline"
              data-testid="receipt-photo-skip"
            >
              <ImageOff size={12} />
              {t('receiptScanner.dialog.skipPhoto')}
            </button>
          </div>
        </>
      )}

      {error ? (
        <Alert
          className="border-[hsl(var(--destructive))]/40 bg-[hsl(var(--destructive))]/10 text-foreground"
          data-testid="receipt-error"
        >
          <AlertCircle className="h-4 w-4 text-[hsl(var(--destructive))]" />
          <AlertDescription className="text-sm">{error}</AlertDescription>
        </Alert>
      ) : null}

      <DialogFooter className="gap-2 sm:gap-3 pt-2">
        <Button
          type="button"
          variant="ghost"
          onClick={() => onOpenChange?.(false)}
          className="text-muted-foreground hover:text-foreground hover:bg-[hsl(var(--muted))]"
          data-testid="receipt-cancel"
        >
          {t('receiptScanner.dialog.cancel')}
        </Button>
      </DialogFooter>
    </div>
  );

  /* --------------------------------------------------------------------- */
  /* Step 2 — Receipt form                                                 */
  /* --------------------------------------------------------------------- */
  const renderFormStep = () => (
    <form
      onSubmit={handleSubmit}
      className="space-y-4 mt-1"
      noValidate
      data-testid="receipt-step-form"
    >
      {/* Photo summary at top */}
      <div className="rounded-md border border-border bg-[hsl(var(--muted))]/40 p-2.5 flex items-center gap-3">
        {photo ? (
          <img
            src={photo}
            alt="Receipt thumbnail"
            className="h-14 w-14 object-cover rounded border border-border shrink-0"
            data-testid="receipt-photo-thumb"
          />
        ) : (
          <div className="h-14 w-14 rounded border border-dashed border-border bg-[hsl(var(--muted))]/60 flex items-center justify-center shrink-0">
            <ImageOff size={18} className="text-muted-foreground" />
          </div>
        )}
        <div className="min-w-0 flex-1">
          <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
            {t('receiptScanner.dialog.photoLabel')}
          </div>
          <div className="text-sm text-foreground truncate">
            {photo ? t('receiptScanner.dialog.photoAttached') : t('receiptScanner.dialog.noPhoto')}
          </div>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => setStep('photo')}
          className="border-border text-foreground hover:bg-[hsl(var(--muted))] text-xs uppercase tracking-[0.16em]"
          data-testid="receipt-change-photo"
        >
          <ArrowLeft size={12} className="mr-1.5" />
          {t('receiptScanner.dialog.change')}
        </Button>
      </div>

      {/* Auto-read didn't work — reassure and point to manual entry. */}
      {scanFailed ? (
        <div
          className="rounded-md border border-[hsl(var(--primary))]/30 bg-[hsl(var(--primary))]/5 px-3 py-2 flex items-center gap-2 text-xs"
          data-testid="receipt-scan-failed-banner"
        >
          <AlertCircle size={13} className="text-[hsl(var(--primary))] shrink-0" />
          <span className="text-foreground">
            Couldn’t auto-read this receipt — just enter the amounts below. Your
            photo is saved.
          </span>
        </div>
      ) : null}

      {/* Scanned banner — AI pre-filled the fields; contractor confirms. */}
      {scanInfo ? (
        <div
          className="rounded-md border border-[hsl(var(--primary))]/30 bg-[hsl(var(--primary))]/5 px-3 py-2 flex items-center gap-2 text-xs"
          data-testid="receipt-scanned-banner"
        >
          <Sparkles size={13} className="text-[hsl(var(--primary))] shrink-0" />
          <span className="text-foreground">
            Scanned from your photo — double-check the details before saving.
          </span>
          {scanInfo.confidence > 0 ? (
            <Badge
              variant="outline"
              className={cn(
                'ml-auto shrink-0 uppercase text-[10px] tracking-[0.16em]',
                scanInfo.confidence >= 0.6
                  ? 'border-[hsl(var(--success))]/40 text-[hsl(var(--success))]'
                  : 'border-[hsl(var(--primary))]/40 text-[hsl(var(--primary))]'
              )}
            >
              {Math.round(scanInfo.confidence * 100)}%
            </Badge>
          ) : null}
        </div>
      ) : null}

      {/* Store */}
      <div className="space-y-2">
        <Label className="text-[11px] font-medium uppercase tracking-[0.18em] text-muted-foreground">
          {t('receiptScanner.dialog.store')} *
        </Label>
        <div
          className="grid grid-cols-2 sm:grid-cols-4 gap-2"
          data-testid="receipt-store-grid"
        >
          {STORES.map((s) => {
            const active = store === s;
            const pct = Math.round((STORE_REBATES[s] || 0) * 100);
            return (
              <button
                type="button"
                key={s}
                onClick={() => setStore(s)}
                aria-pressed={active}
                data-testid={`receipt-store-${s.toLowerCase().replace(/\s+/g, '-')}`}
                className={cn(
                  'rounded-md border px-2.5 py-2 text-xs font-medium uppercase tracking-[0.16em] transition-colors',
                  active
                    ? 'border-[hsl(var(--primary))]/40 bg-[hsl(var(--primary))]/15 text-[hsl(var(--primary))]'
                    : 'border-border bg-[hsl(var(--muted))] text-muted-foreground hover:text-foreground hover:border-[hsl(var(--primary))]/30'
                )}
              >
                <div>{s}</div>
                {pct > 0 ? (
                  <div className="text-[10px] text-[hsl(var(--success))] mt-0.5">
                    {t('receiptScanner.dialog.rebatePct', { pct })}
                  </div>
                ) : (
                  <div className="text-[10px] text-muted-foreground/70 mt-0.5">
                    {t('receiptScanner.dialog.noRebate')}
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Job */}
      <div className="space-y-2">
        <Label className="text-[11px] font-medium uppercase tracking-[0.18em] text-muted-foreground">
          {t('receiptScanner.dialog.job')} *
        </Label>
        <Select value={jobId} onValueChange={setJobId}>
          <SelectTrigger
            className="h-10 bg-[hsl(var(--muted))] border-border"
            data-testid="receipt-job"
          >
            <SelectValue placeholder={t('receiptScanner.dialog.selectActiveJob')} />
          </SelectTrigger>
          <SelectContent className="bg-[hsl(var(--card))] border-border max-h-72">
            {jobs.map((j) => (
              <SelectItem key={j.id} value={j.id}>
                {j.name}
              </SelectItem>
            ))}
            {/* Catch-all buckets for costs not tied to a specific job. Always
                available — created on first use. */}
            {JOB_BUCKETS.map((b) => (
              <SelectItem key={b.option} value={b.option}>
                {b.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <button
          type="button"
          onClick={enterSplit}
          className="inline-flex items-center gap-1.5 text-[11px] text-[hsl(var(--primary))] hover:underline underline-offset-4"
          data-testid="receipt-enter-split"
        >
          <Split size={12} />
          Buy for more than one job? Split items across jobs
        </button>
      </div>

      {/* Subtotal (pre-tax) + Tax — the rebate is computed on the subtotal */}
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-2">
          <Label className="text-[11px] font-medium uppercase tracking-[0.18em] text-muted-foreground">
            Subtotal (pre-tax) *
          </Label>
          <Input
            type="number"
            inputMode="decimal"
            min="0"
            step="0.01"
            value={subtotal}
            onChange={(e) => setSubtotal(e.target.value)}
            placeholder="0.00"
            className="h-10 bg-[hsl(var(--muted))] border-border text-foreground"
            data-testid="receipt-subtotal"
          />
        </div>
        <div className="space-y-2">
          <Label className="text-[11px] font-medium uppercase tracking-[0.18em] text-muted-foreground">
            Tax
          </Label>
          <Input
            type="number"
            inputMode="decimal"
            min="0"
            step="0.01"
            value={tax}
            onChange={(e) => setTax(e.target.value)}
            placeholder="0.00"
            className="h-10 bg-[hsl(var(--muted))] border-border text-foreground"
            data-testid="receipt-tax"
          />
        </div>
      </div>

      {/* Date + computed Total (with tax) = what the job is charged */}
      <div className="grid grid-cols-2 gap-3 items-end">
        <div className="space-y-2">
          <Label className="text-[11px] font-medium uppercase tracking-[0.18em] text-muted-foreground">
            {t('receiptScanner.dialog.date')}
          </Label>
          <Input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="h-10 bg-[hsl(var(--muted))] border-border text-foreground"
            data-testid="receipt-date"
          />
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
            Total (with tax)
          </div>
          <div
            className="text-lg font-semibold text-foreground tabular-nums"
            data-testid="receipt-total"
          >
            {formatUsd(totalNum)}
          </div>
        </div>
      </div>

      {/* Description */}
      <div className="space-y-2">
        <Label className="text-[11px] font-medium uppercase tracking-[0.18em] text-muted-foreground">
          {t('receiptScanner.dialog.description')}
        </Label>
        <Input
          value={desc}
          onChange={(e) => setDesc(e.target.value)}
          placeholder={t('receiptScanner.dialog.descPlaceholder')}
          className="h-10 bg-[hsl(var(--muted))] border-border text-foreground"
          data-testid="receipt-desc"
        />
      </div>

      {/* Live rebate preview — computed on the pre-tax subtotal, or read
          straight off the receipt when the store prints the amount. */}
      <div
        className="rounded-md border border-[hsl(var(--success))]/30 bg-[hsl(var(--success))]/10 px-3 py-2"
        data-testid="receipt-rebate-preview"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-xs text-[hsl(var(--success))]">
            <ArrowDownToLine size={12} />
            <span className="uppercase tracking-[0.16em] font-semibold">
              {t('receiptScanner.dialog.expectedRebate')}
            </span>
            <Badge
              variant="outline"
              className="border-[hsl(var(--success))]/40 text-[hsl(var(--success))] uppercase text-[10px] tracking-[0.16em]"
            >
              {ratePct}%
            </Badge>
          </div>
          <div
            className="text-sm font-semibold text-[hsl(var(--success))] tabular-nums"
            data-testid="receipt-rebate-value"
          >
            {formatUsd(rebate)}
          </div>
        </div>
        {ratePct > 0 || rebateNumber ? (
          <div className="mt-1 flex items-center justify-between text-[10px] text-muted-foreground">
            <span>
              {rebateFromReceipt
                ? 'Read from receipt'
                : ratePct > 0
                ? `${ratePct}% of pre-tax subtotal`
                : ''}
            </span>
            {rebateNumber ? (
              <span data-testid="receipt-rebate-number">
                Rebate #{String(rebateNumber).replace(/^#/, '')}
              </span>
            ) : null}
          </div>
        ) : null}
      </div>

      {error ? (
        <Alert
          className="border-[hsl(var(--destructive))]/40 bg-[hsl(var(--destructive))]/10 text-foreground"
          data-testid="receipt-error"
        >
          <AlertCircle className="h-4 w-4 text-[hsl(var(--destructive))]" />
          <AlertDescription className="text-sm">{error}</AlertDescription>
        </Alert>
      ) : null}

      <DialogFooter className="gap-2 sm:gap-3 pt-2">
        <Button
          type="button"
          variant="ghost"
          onClick={() => onOpenChange?.(false)}
          disabled={submitting}
          className="text-muted-foreground hover:text-foreground hover:bg-[hsl(var(--muted))]"
        >
          {t('receiptScanner.dialog.cancel')}
        </Button>
        <Button
          type="submit"
          disabled={submitting}
          className="bg-primary text-primary-foreground hover:bg-primary/90 uppercase tracking-[0.16em] text-xs"
          data-testid="receipt-submit"
        >
          {submitting ? <Loader2 size={14} className="mr-2 animate-spin" /> : null}
          {t('receiptScanner.dialog.save')}
        </Button>
      </DialogFooter>
    </form>
  );

  /* --------------------------------------------------------------------- */
  /* Step 2b — Split items across jobs                                     */
  /* --------------------------------------------------------------------- */
  const renderSplitStep = () => {
    const reconciled = Math.abs(itemsTotal - subNum) <= 0.01 || subNum <= 0;
    return (
      <form
        onSubmit={handleSaveSplit}
        className="space-y-4 mt-1"
        noValidate
        data-testid="receipt-step-split"
      >
        {/* Receipt photo, readable, so items can be checked against it. */}
        {photo ? (
          <div className="rounded-md border border-border bg-[hsl(var(--muted))]/40 p-2">
            <img
              src={photo}
              alt="Receipt"
              className="block w-full max-h-[42vh] object-contain rounded"
              data-testid="receipt-split-photo"
            />
          </div>
        ) : null}

        <div className="flex items-center justify-between">
          <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
            {store} · assign each item to a job
          </div>
          <button
            type="button"
            onClick={addItem}
            className="inline-flex items-center gap-1 text-[11px] text-[hsl(var(--primary))] hover:underline underline-offset-4"
            data-testid="receipt-split-add"
          >
            <Plus size={12} /> Add item
          </button>
        </div>

        {/* Item rows */}
        <div className="space-y-2" data-testid="receipt-split-items">
          {lineItems.map((it) => (
            <div
              key={it.id}
              className="rounded-md border border-border bg-[hsl(var(--muted))]/30 p-2 space-y-2"
            >
              <div className="flex items-center gap-2">
                <Input
                  value={it.desc}
                  onChange={(e) => updateItem(it.id, { desc: e.target.value })}
                  placeholder="Item"
                  className="h-9 flex-1 bg-[hsl(var(--card))] border-border text-foreground text-sm"
                />
                <Input
                  type="number"
                  inputMode="decimal"
                  min="0"
                  step="0.01"
                  value={it.amount}
                  onChange={(e) => updateItem(it.id, { amount: e.target.value })}
                  placeholder="0.00"
                  className="h-9 w-24 bg-[hsl(var(--card))] border-border text-foreground text-sm tabular-nums"
                />
                <button
                  type="button"
                  onClick={() => removeItem(it.id)}
                  className="text-muted-foreground hover:text-[hsl(var(--destructive))] p-1.5 shrink-0"
                  aria-label="Remove item"
                >
                  <Trash2 size={15} />
                </button>
              </div>
              <Select
                value={it.jobId || undefined}
                onValueChange={(v) => updateItem(it.id, { jobId: v })}
              >
                <SelectTrigger
                  className={cn(
                    'h-9 bg-[hsl(var(--card))] border-border text-sm',
                    !it.jobId && 'border-[hsl(var(--primary))]/50 text-[hsl(var(--primary))]'
                  )}
                >
                  <SelectValue placeholder="Assign to a job…" />
                </SelectTrigger>
                <SelectContent className="bg-[hsl(var(--card))] border-border max-h-64">
                  {jobs.map((j) => (
                    <SelectItem key={j.id} value={j.id}>
                      {j.name}
                    </SelectItem>
                  ))}
                  {JOB_BUCKETS.map((b) => (
                    <SelectItem key={b.option} value={b.option}>
                      {b.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ))}
          {lineItems.length === 0 ? (
            <div className="text-center text-xs text-muted-foreground py-4">
              No items yet — tap “Add item” to start.
            </div>
          ) : null}
        </div>

        {/* Reconciliation vs the scanned subtotal */}
        <div
          className={cn(
            'flex items-center justify-between text-[11px] rounded-md px-3 py-2 border',
            reconciled
              ? 'border-border text-muted-foreground bg-[hsl(var(--muted))]/30'
              : 'border-[hsl(var(--primary))]/40 text-[hsl(var(--primary))] bg-[hsl(var(--primary))]/5'
          )}
          data-testid="receipt-split-reconcile"
        >
          <span>Items total</span>
          <span className="tabular-nums">
            {formatUsd(itemsTotal)}
            {subNum > 0 ? (
              <span className="text-muted-foreground"> / subtotal {formatUsd(subNum)}</span>
            ) : null}
          </span>
        </div>

        {/* Per-job preview */}
        {splitGroups.length > 0 ? (
          <div className="rounded-md border border-border overflow-hidden" data-testid="receipt-split-preview">
            <div className="px-3 py-1.5 bg-[hsl(var(--muted))]/50 text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
              How it will be logged
            </div>
            <div className="divide-y divide-border">
              {splitGroups.map((g) => {
                const gRebate = receiptRebate({
                  store,
                  subtotal: g.subtotal,
                  tax: g.tax,
                  rebateAmount: g.rebateAmount,
                });
                return (
                  <div key={g.jobId} className="px-3 py-2 flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-sm text-foreground truncate">{jobLabelById(g.jobId)}</div>
                      <div className="text-[10px] text-muted-foreground">
                        {g.items.length} item{g.items.length > 1 ? 's' : ''} · tax {formatUsd(g.tax)}
                        {gRebate > 0 ? ` · rebate ${formatUsd(gRebate)}` : ''}
                      </div>
                    </div>
                    <div className="text-sm font-semibold text-foreground tabular-nums shrink-0">
                      {formatUsd(Math.round((g.subtotal + g.tax) * 100) / 100)}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : null}

        {error ? (
          <Alert
            className="border-[hsl(var(--destructive))]/40 bg-[hsl(var(--destructive))]/10 text-foreground"
            data-testid="receipt-error"
          >
            <AlertCircle className="h-4 w-4 text-[hsl(var(--destructive))]" />
            <AlertDescription className="text-sm">{error}</AlertDescription>
          </Alert>
        ) : null}

        <DialogFooter className="gap-2 sm:gap-3 pt-2">
          <Button
            type="button"
            variant="ghost"
            onClick={() => setStep('form')}
            disabled={submitting}
            className="text-muted-foreground hover:text-foreground hover:bg-[hsl(var(--muted))]"
            data-testid="receipt-split-back"
          >
            <ArrowLeft size={14} className="mr-1.5" />
            Back
          </Button>
          <Button
            type="submit"
            disabled={submitting || !allItemsAssigned}
            className="bg-primary text-primary-foreground hover:bg-primary/90 uppercase tracking-[0.16em] text-xs"
            data-testid="receipt-split-save"
          >
            {submitting ? <Loader2 size={14} className="mr-2 animate-spin" /> : null}
            Save split
          </Button>
        </DialogFooter>
      </form>
    );
  };

  // Block every close attempt while a photo is being captured/read/scanned —
  // no matter which internal event triggers it — so the phone camera's spurious
  // dismiss on return can't drop the user out of the flow. Normal closes (X,
  // Cancel, Escape) still work at every other moment.
  const guardedOpenChange = (next) => {
    if (!next && (cameraPending || readingPhoto || scanning)) return;
    onOpenChange?.(next);
  };

  return (
    <Dialog open={open} onOpenChange={guardedOpenChange}>
      <DialogContent
        className="bg-[hsl(var(--card))] border-[hsl(var(--hairline))] max-w-md max-h-[92vh] overflow-y-auto"
        data-testid="log-receipt-dialog"
        onInteractOutside={(e) => e.preventDefault()}
        onFocusOutside={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 text-[hsl(var(--primary))]">
              <ReceiptIcon size={16} strokeWidth={2} />
              <span className="text-[10px] font-medium uppercase tracking-[0.28em] text-muted-foreground">
                {t('receiptScanner.dialog.logReceipt')}
              </span>
              <span className="text-[9px] text-muted-foreground/50 tracking-wide">
                {APP_BUILD}
              </span>
            </div>
            <div className="flex items-center gap-1.5" aria-label="Steps">
              <span
                className={cn(
                  'h-1.5 rounded-full transition-all',
                  step === 'photo'
                    ? 'w-6 bg-[hsl(var(--primary))]'
                    : 'w-3 bg-[hsl(var(--primary))]/30'
                )}
                data-testid="receipt-step-indicator-photo"
              />
              <span
                className={cn(
                  'h-1.5 rounded-full transition-all',
                  step === 'form' || step === 'split'
                    ? 'w-6 bg-[hsl(var(--primary))]'
                    : 'w-3 bg-[hsl(var(--primary))]/30'
                )}
                data-testid="receipt-step-indicator-form"
              />
            </div>
          </div>
          <DialogTitle className="text-foreground tracking-tight">
            {step === 'photo'
              ? t('receiptScanner.dialog.stepPhotoTitle')
              : step === 'split'
              ? 'Split across jobs'
              : t('receiptScanner.dialog.stepFormTitle')}
          </DialogTitle>
          <DialogDescription className="text-muted-foreground">
            {step === 'photo'
              ? t('receiptScanner.dialog.stepPhotoDesc')
              : step === 'split'
              ? 'Assign each item to the job it belongs to — tax and rebate are split by each job’s share.'
              : t('receiptScanner.dialog.stepFormDesc')}
          </DialogDescription>
        </DialogHeader>

        {step === 'photo'
          ? renderPhotoStep()
          : step === 'split'
          ? renderSplitStep()
          : renderFormStep()}
      </DialogContent>
    </Dialog>
  );
};

export default LogReceiptDialog;
